import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { RefreshCcw } from "lucide-react";
import { Arrow } from "@/components/icons";
import { routeHead } from "@/components/seo";
import { pageMeta, assessment, site } from "@/content";
import { ScoreGauge } from "@/components/score-gauge";

const ROUTE = "/financial-blind-spot-finder";
export const Route = createFileRoute("/financial-blind-spot-finder")({
  head: () => routeHead(ROUTE),
  component: Page,
});

const NEXT_STEP_BY_CATEGORY: Record<string, string> = {
  reporting:
    "Start with a Strategic Finance Review focused on your reporting: what you get, how often, and whether it actually explains what moved.",
  operations:
    "Start with a Strategic Finance Review focused on the day to day: reconciliation, BAS and IAS process, and how bills and invoices are tracked.",
  controls:
    "Start with a Strategic Finance Review focused on resilience: what happens to your finance function if the one person running it is unavailable.",
};

function Page() {
  const p = pageMeta(ROUTE)!;
  const questions = assessment.questions;
  const scale = assessment.answerScale;
  const categories = assessment.categories;

  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    Array(questions.length).fill(null),
  );
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);

  const answered = answers.filter((a) => a !== null).length;
  const progress = Math.round((answered / questions.length) * 100);

  const totalScore = useMemo(
    () => answers.reduce<number>((sum, v) => sum + (v ?? 0), 0),
    [answers],
  );

  function choose(score: number) {
    const next = [...answers];
    next[current] = score;
    setAnswers(next);
    if (current < questions.length - 1) {
      setCurrent(current + 1);
    } else {
      setDone(true);
    }
  }

  function reset() {
    setAnswers(Array(questions.length).fill(null));
    setCurrent(0);
    setDone(false);
  }

  const result = useMemo(
    () => assessment.results.find((r) => totalScore >= r.minimum && totalScore <= r.maximum),
    [totalScore],
  );

  const categoryBreakdown = useMemo(() => {
    const byCategory = new Map<string, { earned: number; possible: number }>();
    questions.forEach((q, i) => {
      const entry = byCategory.get(q.category) ?? { earned: 0, possible: 0 };
      entry.earned += answers[i] ?? 0;
      entry.possible += 2;
      byCategory.set(q.category, entry);
    });
    return Array.from(byCategory.entries())
      .map(([key, { earned, possible }]) => ({
        key,
        label: categories[key as keyof typeof categories] ?? key,
        pct: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      }))
      .sort((a, b) => b.pct - a.pct);
  }, [answers, categories, questions]);

  const strongest = categoryBreakdown[0];
  const weakest = categoryBreakdown[categoryBreakdown.length - 1];
  const scoreOutOf100 = Math.round((totalScore / assessment.scoreMaximum) * 100);

  if (done && result) {
    return (
      <div className="bg-background text-foreground">
        <section className="border-b border-line">
          <div className="mx-auto max-w-3xl px-6 py-20">
            <div className="mt-6 flex flex-col items-start gap-8 sm:flex-row sm:items-center">
              <ScoreGauge value={totalScore} max={assessment.scoreMaximum} />
              <div>
                <p className="font-mono text-sm text-subtle">{scoreOutOf100}/100</p>
                <h1 className="mt-1 font-serif text-3xl leading-[1.1] text-ink sm:text-4xl">
                  {result.title}
                </h1>
                <p className="mt-3 text-lg leading-relaxed text-ink/80">{result.summary}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-line">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <h2 className="font-serif text-xl text-ink">Where this comes from</h2>
            <div className="mt-8 space-y-6">
              {categoryBreakdown.map((c) => (
                <div key={c.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-ink">{c.label}</span>
                    <span className="font-mono text-subtle">{c.pct}%</span>
                  </div>
                  <div className="mt-2 h-1 w-full bg-line">
                    <div className="h-full bg-teal" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {strongest && weakest && (
              <div className="mt-10 grid gap-8 border-t border-line pt-8 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-subtle">Strongest area</p>
                  <p className="mt-1 font-serif text-lg text-ink">{strongest.label}</p>
                </div>
                <div>
                  <p className="text-xs text-subtle">Priority area</p>
                  <p className="mt-1 font-serif text-lg text-ink">{weakest.label}</p>
                </div>
              </div>
            )}

            {weakest && (
              <p className="mt-8 max-w-xl border-t border-line pt-8 leading-relaxed text-ink/75">
                <span className="font-medium text-ink">Recommended next step: </span>
                {NEXT_STEP_BY_CATEGORY[weakest.key] ??
                  "A Strategic Finance Review is the fastest way to work out what to fix first."}
              </p>
            )}
          </div>
        </section>

        <section className="border-b border-line">
          <div className="mx-auto max-w-3xl px-6 py-16">
            <div className="flex flex-wrap gap-3">
              <Link to={site.primaryCta.href} className="btn-primary">
                {site.primaryCta.label}
                <Arrow className="h-4 w-4" />
              </Link>
              <button type="button" onClick={reset} className="btn-secondary">
                <RefreshCcw className="h-4 w-4" /> Retake the assessment
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const q = questions[current]!;
  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-line">
        <div className="mx-auto max-w-3xl px-6 py-16">
          <h1 className="mt-4 font-serif text-4xl leading-[1.1] text-ink sm:text-5xl">{p.h1}</h1>
          <p className="mt-6 text-lg text-ink/80">{p.description}</p>
        </div>
      </section>

      <section className="horizon-field--light">
        <div className="relative z-10 mx-auto max-w-2xl px-6 py-16">
          <div className="mb-6 flex items-center justify-between text-sm text-subtle">
            <span>
              Question {current + 1} of {questions.length}
            </span>
            <span>{progress}% complete</span>
          </div>
          <div className="h-1 w-full bg-line">
            <div className="h-full bg-teal" style={{ width: `${progress}%` }} />
          </div>

          <div className="card-premium mt-8 p-8">
            <h2 className="font-serif text-xl text-ink sm:text-2xl">{q.text}</h2>
            <div className="mt-6 grid gap-3">
              {scale.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => choose(s.score)}
                  className="card-premium flex items-center justify-between px-5 py-4 text-left text-ink transition-colors hover:border-teal hover:bg-stone/40"
                >
                  <span className="font-medium">{s.label}</span>
                  <Arrow className="h-4 w-4 text-teal" />
                </button>
              ))}
            </div>

            {current > 0 && (
              <button
                type="button"
                onClick={() => setCurrent(current - 1)}
                className="mt-6 text-sm font-medium text-teal hover:text-ink"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
